/**
 * Audio Uploader — spec FR-001
 * Accepts MP3, WAV, M4A, FLAC, AAC; shows duration estimate.
 */

import { useState, useCallback } from "react";
import { FileUploader } from "./FileUploader";
import { uploadFiles, type UploadProgress, formatFileSize, formatDuration } from "@/lib/storage/upload";

interface AudioUploaderProps {
  projectId: string;
  onUploadComplete: (storagePaths: string[]) => void;
  onError?: (error: string) => void;
}

const AUDIO_ACCEPT = ".mp3,.wav,.m4a,.flac,.aac,audio/mpeg,audio/wav,audio/mp4,audio/flac,audio/aac";

export function AudioUploader({ projectId, onUploadComplete, onError }: AudioUploaderProps) {
  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    setFiles(selectedFiles);
    // Initialize progress for display
    setProgress(
      selectedFiles.map((f) => ({
        file_name: f.name,
        bytes_uploaded: 0,
        total_bytes: f.size,
        percent: 0,
        status: "preparing" as const,
      })),
    );
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || uploading) return;

    setUploading(true);
    try {
      const paths = await uploadFiles(files, projectId, "audio", (fileIndex, fileProgress) => {
        setProgress((prev) => {
          const next = [...prev];
          next[fileIndex] = fileProgress;
          return next;
        });
      });
      onUploadComplete(paths);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not add audio source.";
      onError?.(message);
    } finally {
      setUploading(false);
    }
  }, [files, projectId, uploading, onUploadComplete, onError]);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  // Rough estimate: 1MB of audio ≈ 1 minute for MP3 at 128kbps
  const estimatedDuration = totalSize / (1024 * 1024) * 60;

  return (
    <div className="space-y-4">
      <FileUploader
        accept={AUDIO_ACCEPT}
        fileType="audio"
        onFilesSelected={handleFilesSelected}
        uploadProgress={progress}
        label="Drop voice or audio source here, or browse"
        description="MP3, WAV, M4A, FLAC, AAC - up to 20 hours total"
      />

      {files.length > 0 && (
        <div className="flex flex-col justify-between gap-3 rounded-as-2 bg-paper-2 px-3 py-3 sm:flex-row sm:items-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
            {files.length} file{files.length > 1 ? "s" : ""} · {formatFileSize(totalSize)}
            {estimatedDuration > 0 && <> · ~{formatDuration(estimatedDuration)}</>}
          </div>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="inline-flex items-center justify-center rounded-as-2 bg-ink-1 px-[15px] py-[8px] font-ui text-t-14 font-semibold text-paper-raised shadow-as-1 transition-colors duration-as-1 hover:bg-walnut-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading ? "Adding source..." : "Add audio to the source"}
          </button>
        </div>
      )}
    </div>
  );
}
